import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS = {
    active: { label: "In corso", color: "rgb(var(--tc-7ce38b))" },
    scheduled: { label: "In programma", color: "rgb(var(--tc-2bb6ff))" },
    past: { label: "Passata", color: "rgb(var(--tc-8f95a3))" },
    canceled: { label: "Annullata", color: "rgb(var(--tc-ff5c5c))" },
};

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date((iso.length <= 10 ? iso + "T00:00:00" : iso)).toLocaleDateString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric",
        });
    } catch { return iso; }
}

function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

export default function AdminAssenze() {
    const [data, setData] = useState({ absences: [], counts: {}, total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        api.get("/admin/absences")
            .then(({ data: d }) => setData(d || { absences: [] }))
            .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)))
            .finally(() => setLoading(false));
    }, []);

    const counts = data.counts || {};
    const rows = (data.absences || []).filter((a) => filter === "all" || a.status === filter);

    return (
        <div data-testid="admin-assenze">
            <div className="op-label text-tb084ff">// GESTIONE PRESIDENTI</div>
            <h2 className="op-heading text-3xl mt-2">Storico Assenze</h2>
            <p className="text-t8f95a3 text-sm mt-2 max-w-2xl">
                Tutte le assenze impostate dalle squadre (in corso, in programma, passate e annullate).
                Quando un presidente si mette in assenza le partite vengono spostate in avanti; qui puoi
                controllarle tutte a colpo d&apos;occhio. Sola consultazione.
            </p>

            {/* Riepilogo + filtri */}
            <div className="flex flex-wrap gap-2 mt-5" data-testid="assenze-filters">
                {[
                    ["all", `Tutte (${data.total || 0})`],
                    ["active", `In corso (${counts.active || 0})`],
                    ["scheduled", `In programma (${counts.scheduled || 0})`],
                    ["past", `Passate (${counts.past || 0})`],
                    ["canceled", `Annullate (${counts.canceled || 0})`],
                ].map(([key, label]) => (
                    <button
                        key={key}
                        onClick={() => setFilter(key)}
                        className={`op-label text-[11px] px-3 py-1.5 border transition-colors ${
                            filter === key
                                ? "border-t2bb6ff text-tffffff bg-t2bb6ff/10"
                                : "border-t22252a text-t8f95a3 hover:text-tffffff hover:border-t3a3d44"
                        }`}
                        data-testid={`assenze-filter-${key}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {error && (
                <div className="mt-4 op-card p-3 border-l-4 border-tff5c5c text-tff5c5c op-mono text-xs" data-testid="assenze-error">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="op-label text-t8f95a3 animate-pulse mt-6">// CARICAMENTO ASSENZE…</div>
            ) : rows.length === 0 ? (
                <div className="op-card p-8 text-center op-mono text-sm text-t8f95a3 mt-6" data-testid="assenze-empty">
                    Nessuna assenza {filter !== "all" ? "con questo filtro" : "registrata"}.
                </div>
            ) : (
                <div className="op-card mt-6 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="assenze-table">
                        <thead>
                            <tr className="op-label text-[10px] text-t5c6370 border-b border-t22252a">
                                <th className="text-left px-4 py-3">Squadra</th>
                                <th className="text-left px-4 py-3">Stato</th>
                                <th className="text-left px-4 py-3">Periodo</th>
                                <th className="text-center px-4 py-3">Giorni</th>
                                <th className="text-center px-4 py-3">Partite spostate</th>
                                <th className="text-left px-4 py-3">Creata da</th>
                                <th className="text-left px-4 py-3">Note</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-t16181c">
                            {rows.map((a) => {
                                const s = STATUS[a.status] || STATUS.past;
                                return (
                                    <tr key={a.id} className="hover:bg-t16181c/40 transition-colors" data-testid={`assenza-row-${a.id}`}>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2 min-w-0">
                                                {a.team_logo ? (
                                                    <img src={a.team_logo} alt="" className="w-6 h-6 object-contain shrink-0" />
                                                ) : (
                                                    <span className="w-6 h-6 shrink-0 rounded-full bg-t16181c border border-t22252a" />
                                                )}
                                                <span className="text-tffffff truncate">{a.team_name || "—"}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className="op-label text-[10px] px-2 py-0.5 border inline-block"
                                                style={{ color: s.color, borderColor: s.color }}
                                                data-testid={`assenza-status-${a.id}`}
                                            >
                                                {s.label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 op-mono text-xs text-t8f95a3 whitespace-nowrap">
                                            {fmtDate(a.start_date)} → {fmtDate(a.end_date)}
                                        </td>
                                        <td className="px-4 py-3 text-center op-mono text-xs text-tffffff">{a.duration_days ?? "—"}</td>
                                        <td className="px-4 py-3 text-center op-mono text-xs text-tffffff">{a.affected_count ?? 0}</td>
                                        <td className="px-4 py-3 op-mono text-[11px] text-t8f95a3">
                                            <div className="truncate max-w-[180px]" title={a.created_by || ""}>{a.created_by || "—"}</div>
                                            <div className="text-t5c6370">{fmtDateTime(a.created_at)}</div>
                                        </td>
                                        <td className="px-4 py-3 op-mono text-[11px] text-t8f95a3">
                                            {a.status === "canceled" ? (
                                                <div className="text-tff5c5c">
                                                    Annullata da {a.canceled_by || "—"}
                                                    <div className="text-t5c6370">{fmtDateTime(a.canceled_at)}</div>
                                                </div>
                                            ) : "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
